import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Home } from 'lucide-react';
import { toast } from 'sonner';
import PlayerFollowButton from '../components/PlayerFollowButton';

const Following = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await apiClient.get('/follow/following');
      setRows(res.data.following || []);
    } catch (e) {
      console.error('Failed to load following', e?.response?.data || e);
      toast.error('Failed to load following list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const getTierColor = (tier) => {
    const t = tier?.toLowerCase();
    if (t === 'elite') return 'text-purple-600 border-purple-300 bg-purple-50';
    if (t === 'baller') return 'text-blue-600 border-blue-300 bg-blue-50';
    if (t === 'role player') return 'text-green-600 border-green-300 bg-green-50';
    return 'text-gray-600 border-gray-300 bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/profile')}
            className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FFFFFF] rounded-[12px] gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-8 w-8 rounded-lg"
            data-testid="nav-home-btn"
            aria-label="Dashboard"
          >
            <Home className="w-4 h-4 text-zinc-400" />
          </Button>
        </div>

        <section className="bg-[#FFFFFF] rounded-[16px] border border-[#E4E4E7] p-6 sm:p-8">
          <h1 className="text-[24px] sm:text-[32px] font-bold text-[#111111] tracking-[-0.02em] mb-6">Following</h1>

          {loading ? (
            <div className="text-center py-8 text-[#71717A]">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#52525B]">You're not following anyone yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map(u => (
                <div
                  key={u.id}
                  className="flex items-center justify-between p-4 border border-[#E4E4E7] rounded-[12px] hover:bg-[#FAFAFA] transition-colors"
                >
                  <div className="flex-1">
                    <div 
                      className="font-semibold text-[16px] text-[#111111] hover:text-[#F97316] cursor-pointer transition-colors"
                      onClick={() => navigate(`/player/${u.username}`)}
                    >
                      {u.display_name || u.username}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <span className={`text-[12px] px-2 py-1 rounded-full border ${getTierColor(u.player_tier)}`}>
                        {u.player_tier || 'Unranked'}
                      </span>
                      <span className="text-[12px] text-[#71717A]">
                        {u.age_group || ''}
                      </span>
                    </div>
                  </div>
                  <PlayerFollowButton
                    userId={u.id}
                    username={u.username}
                    isFollowing={true}
                    size="sm"
                    onFollowChange={(uid, newState) => {
                      if (!newState.following) {
                        // Remove from the list when unfollowed
                        setRows(prev => prev.filter(r => r.id !== uid));
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Following;
